import { Callout } from '@repair-please/ui';

export interface EnvironmentBannerProps {
  /**
   * Defaults to the compile-time `__RP_ENVIRONMENT__` define — every real
   * caller (`app.tsx`) renders `<EnvironmentBanner />` with no prop, so real
   * behaviour is unchanged by this prop's existence. Accepting it as an
   * override exists only so a test can render the production-suppressed
   * branch directly, in the same process as every other case: vitest
   * resolves `__RP_ENVIRONMENT__` once, at startup, from whatever
   * `RP_ENVIRONMENT` happens to be set to there, so it alone cannot exercise
   * both branches in one run.
   */
  readonly environment?: 'production' | 'staging' | 'development';
  /**
   * Defaults to the compile-time `__RP_DEPLOY_ID__` define, for the same
   * reason and by the same pattern as `environment` above: every real caller
   * renders with no prop, so real behaviour is unchanged, and the override
   * exists only so a test can assert the exact rendered SHA against a known
   * value instead of merely proving *some* string was interpolated
   * (cross-review finding F003, this PR — the supplied banner test asserted
   * the environment name and warning text but never the SHA itself, so a
   * regression replacing it with a constant like `unknown` passed every
   * existing gate).
   */
  readonly deployId?: string;
}

/**
 * The persistent, non-dismissible banner ADR 0026 §3 requires on every
 * non-production build: the environment name, the deploy identifier, and the
 * fixed warning that this is not the live service.
 *
 * IT SAID "test data only" AND NO LONGER DOES, which is a correction rather
 * than a softening (cross-review F001, round 64). That phrasing is a claim
 * about the state of a database, and the controls behind it are
 * forward-looking: `POST /api/waitlist` refuses a non-production write, and
 * nothing audits or deletes a row a previous deploy already accepted — that is
 * issue #210, and it is open. A banner asserting a guarantee no control holds
 * is worse than one that does not, because a reader believes it. What replaces
 * it is true of this deploy and is the sentence a visitor can act on: this is
 * not the live service, so do not type anything real into it.
 *
 * It is the control that stops a screenshot of staging — or of a laptop's own
 * `pnpm dev` — from being mistaken for evidence about production.
 *
 * Deliberately not dismissible: there is no state here, no close button and
 * no way to suppress it, because the whole point is that nobody who forgot
 * it was on can turn it off.
 *
 * Built on `Callout`'s `caution` tone rather than new markup, per the ADR:
 * reuse the provenance-checked amber tokens already in `packages/ui`, do not
 * invent new colours. `live` stays at `Callout`'s own default (`'off'`) —
 * the component's own guidance is that standing context present before the
 * page finishes loading is not an event to announce.
 */
export function EnvironmentBanner({
  environment = __RP_ENVIRONMENT__,
  deployId = __RP_DEPLOY_ID__,
}: EnvironmentBannerProps = {}) {
  if (environment === 'production') return null;

  return (
    <Callout tone="caution" density="compact" className="app-environment-banner">
      {environment.toUpperCase()} — build {deployId} — not the live service. Do not enter real
      personal information.
    </Callout>
  );
}
